/**
 * Transfer Auto Restock service
 */
import { Injectable, Injector } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { first, flow, head } from 'lodash';

import { BaseService } from '../base';
import {
  IBaseEntityType,
  IEntityConfig,
  ITransfer,
  ObjectId,
} from '@shared/types';
import { config, entityType } from './transfer-auto-restock.service.config';
import { lazyInject } from '@containers/base/decorators';
import { TransferService } from '../transfer/transfer.service';
import { EventsService } from '../events/events.service';
import { StandWorksheetService } from '../stand-worksheet/stand-worksheet.service';
import { IStandWorkSheetItem } from '@shared/types/entities/stand-worksheet';
import { rx } from '@shared/rx';

/**
 * Transfer service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class TransferAutoRestockService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = entityType;

  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(EventsService) public eventsService: EventsService;
  @lazyInject(StandWorksheetService)
  public standWorksheetService: StandWorksheetService;

  public quickFilterFields: string[] = ['name'];
  public listProjection: string[] = ['name'];
  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];

  public constructor(public injector: Injector) {
    super(injector);
  }

  public frontToBackItemMapper(doc: ITransfer): IBaseEntityType {
    return flow(
      this.transferService.processDocWithDMStructure.bind(this),
      this.transferService.processNewStatusDocument.bind(this),
      super.frontToBackItemMapper.bind(this),
    )(doc);
  }

  public getLastEventBySiteId(siteId: ObjectId): any {
    return this.eventsService
      .fetchList({
        filter: `status eq in(open,closed)) and ((availability_conditions/condition eq 'all') or ((availability_conditions/condition eq 'include') and (availability_conditions/sites eq in(${siteId}))) or ((availability_conditions/condition eq 'exclude') and (availability_conditions/sites ne in(${siteId})))`,
        select: ['status', 'id'],
        orderby: `event_days/start_date_time desc`,
        top: 1,
      })
      .pipe(
        rx.map((response) => {
          return head(response.items);
        }),
        rx.first(),
      )
      .toPromise();
  }

  public getRecentStandWorksheet(
    eventId: ObjectId,
    siteId: ObjectId,
  ): Promise<IBaseEntityType> {
    return this.standWorksheetService
      .fetchList({
        filter: `event_id eq objectid(${eventId}) and site_id eq objectid(${siteId})`,
        orderby: 'updated_at desc',
        select: ['entity_id', 'formula_version'],
        top: 1,
      })
      .pipe(rx.map((response) => first(response.items)))
      .toPromise();
  }

  public getStandWorksheetItemsById(
    entityId: ObjectId,
  ): Promise<IStandWorkSheetItem[]> {
    return this.standWorksheetService
      .getStandSheetItemList({
        filter: `stand_work_sheet_entity_id eq objectid(${entityId})`,
        select: [
          'manager_begin',
          'begin',
          'pre_event_in',
          'end',
          'during_event_in',
          'during_event_out',
          'adjustment',
          'gratis',
          'ingredient_spoil',
          'sale',
          'sale_post_closure',
          'inventory_item_entity_id',
        ],
      })
      .toPromise();
  }

  protected getPayloadBlackList(): string[] {
    return [
      'inventory_items.[each].gl_account_entity_id',
      'inventory_items.[each].gl_account_name',
      'inventory_items.[each].item_name',
      'inventory_items.[each].major_category_entity_id',
      'inventory_items.[each].major_category_name',
      'inventory_items.[each].measure_code',
      'inventory_items.[each].measure_description',
      'inventory_items.[each].measure_factor_value',
      'inventory_items.[each].measure_id',
      'inventory_items.[each].minor_category_entity_id',
      'inventory_items.[each].minor_category_name',
      'inventory_items.[each].major_category_external_id',
      'inventory_items.[each].minor_category_external_id',
    ];
  }
}
